import { useEffect, useState } from "react";
import { ApiError, type Tokens } from "./api.ts";
import { resume, signIn, signOut, signUp, subject } from "./auth.ts";

type State = { kind: "loading" } | { kind: "signedOut"; error?: string } | { kind: "signedIn"; tokens: Tokens };

function message(e: unknown): string {
  if (e instanceof DOMException && e.name === "NotAllowedError") return "Passkey request was cancelled.";
  if (e instanceof ApiError && e.status === 429) return "Too many attempts. Wait a few minutes and try again.";
  if (e instanceof ApiError) return e.message;
  return "Something went wrong. Try again.";
}

export function App() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    resume().then(t => setState(t ? { kind: "signedIn", tokens: t } : { kind: "signedOut" }));
  }, []);

  const run = async (f: () => Promise<{ tokens: Tokens }>) => {
    setBusy(true);
    try {
      const s = await f();
      setState({ kind: "signedIn", tokens: s.tokens });
    } catch (e) {
      setState({ kind: "signedOut", error: message(e) });
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === "loading") return <main className="card"><p className="muted">Loading…</p></main>;

  if (state.kind === "signedIn") {
    return (
      <main className="card">
        <h1>K8 HQ</h1>
        <p>You're signed in.</p>
        <p className="muted small">Account {subject(state.tokens.accessToken)}</p>
        <p className="muted">Your mailbox will appear here.</p>
        <div className="row">
          <button onClick={() => signOut(state.tokens.accessToken).finally(() => setState({ kind: "signedOut" }))}>Sign out</button>
          <button className="secondary" onClick={() => signOut(state.tokens.accessToken, true).finally(() => setState({ kind: "signedOut" }))}>Sign out everywhere</button>
        </div>
      </main>
    );
  }

  return (
    <main className="card">
      <h1>K8 HQ</h1>
      <p className="muted">Sign in with a passkey. No password, no email needed.</p>
      <button disabled={busy} onClick={() => run(signIn)}>Sign in with passkey</button>
      <hr />
      <label htmlFor="name">New here? Your name (optional)</label>
      <input id="name" value={name} maxLength={100} autoComplete="name" onChange={e => setName(e.target.value)} />
      <button className="secondary" disabled={busy} onClick={() => run(() => signUp(name.trim()))}>Create account</button>
      {state.error && <p className="error" role="alert">{state.error}</p>}
    </main>
  );
}
